import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ShieldCheck, LogIn } from "lucide-react";
import { api, errMsg, signOut, SESSION_KEY, TRUST_KEY } from "../lib/api";

const shell = "min-h-screen flex items-center justify-center p-6 bg-[#090D16] text-[#E5E7EB]";
const card = "w-full max-w-md p-8 rounded-md border-2 border-[#1E293B] bg-[#0F1623]";
const REMEMBER_KEY = "ff-remember-device";

const framed = () => { try { return window.self !== window.top; } catch { return true; } };

function googleLogin() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/";
  const url = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  if (!(framed() && window.open(url, "ff-google-signin", "width=520,height=680"))) window.location.href = url;
}

const exchange = (sid) => api.post("/auth/session", { session_id: sid }).then((r) => { localStorage.setItem(SESSION_KEY, r.data.session_token); return r.data; });

function Brand() {
  return <div className="font-display text-3xl font-extrabold mb-1"><span className="text-white">FLOW</span><span className="text-[#F59E0B]">FORGE</span></div>;
}

function Login({ error }) {
  const [remember, setRemember] = useState(() => localStorage.getItem(REMEMBER_KEY) === "1");
  return (
    <div className={shell} data-testid="login-screen">
      <div className={card}>
        <Brand />
        <p className="text-sm text-[#9CA3AF] mb-6">Sign in with your authorized Google account. A 6-digit Google Authenticator code is required.</p>
        {error && <div data-testid="login-error" className="mb-4 p-3 rounded-md border border-[#EF4444] text-sm text-[#FCA5A5]">{error}</div>}
        <button data-testid="google-login-btn" onClick={googleLogin} className="h-14 w-full rounded-md bg-white text-[#090D16] font-bold flex items-center justify-center gap-2"><LogIn className="w-5 h-5" />Sign in with Google</button>
        <label className="flex items-center gap-2 mt-4 text-sm cursor-pointer">
          <input data-testid="remember-device-checkbox" type="checkbox" className="w-5 h-5 accent-[#F59E0B]" checked={remember}
            onChange={(e) => { setRemember(e.target.checked); localStorage.setItem(REMEMBER_KEY, e.target.checked ? "1" : "0"); }} />
          Remember this device for 30 days
        </label>
      </div>
    </div>
  );
}

function TwoFactor({ me, onDone }) {
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(() => localStorage.getItem(REMEMBER_KEY) === "1");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (me.mfa_setup_needed) api.post("/auth/2fa/setup").then((r) => setSetup(r.data)).catch((e) => setErr(errMsg(e))); }, [me.mfa_setup_needed]);
  const verify = async () => {
    setBusy(true); setErr("");
    try {
      localStorage.setItem(REMEMBER_KEY, remember ? "1" : "0");
      const d = (await api.post("/auth/2fa/verify", { code, remember_device: remember })).data;
      if (d.trusted_token) localStorage.setItem(TRUST_KEY, d.trusted_token);
      onDone(d);
    }
    catch (e) { setErr(errMsg(e)); setCode(""); } finally { setBusy(false); }
  };
  return (
    <div className={shell} data-testid="twofactor-screen">
      <div className={card}>
        <Brand />
        <div className="flex items-center gap-2 mb-4 text-[#F59E0B] font-bold"><ShieldCheck className="w-5 h-5" />{me.mfa_setup_needed ? "Set up Google Authenticator" : "Two-factor verification"}</div>
        {me.mfa_setup_needed && setup && (
          <div className="mb-4 text-sm text-[#9CA3AF]">
            Scan this code in Google Authenticator, then enter the 6-digit code it shows.
            <img data-testid="totp-qr" src={setup.qr_png} alt="Authenticator QR code" className="mt-3 w-48 h-48 bg-white p-2 rounded-md" />
            <div className="mt-2 font-mono text-xs break-all" data-testid="totp-secret">Key: {setup.secret}</div>
          </div>
        )}
        <div className="text-xs text-[#6B7280] mb-2">Signed in as {me.user.email}</div>
        <input data-testid="totp-code-input" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          onKeyDown={(e) => e.key === "Enter" && code.length === 6 && verify()} placeholder="123456"
          className="h-14 w-full text-center tracking-[0.5em] font-mono text-2xl bg-[#090D16] border border-[#334155] rounded-md focus:outline-none focus:border-[#F59E0B]" />
        {err && <div data-testid="totp-error" className="mt-3 text-sm text-[#FCA5A5]">{err}</div>}
        <label className="flex items-center gap-2 mt-4 text-sm cursor-pointer">
          <input data-testid="totp-remember-checkbox" type="checkbox" className="w-5 h-5 accent-[#F59E0B]" checked={remember} onChange={(e) => setRemember(e.target.checked)} />Remember this device for 30 days
        </label>
        <button data-testid="totp-verify-btn" disabled={code.length !== 6 || busy} onClick={verify} className="mt-4 h-12 w-full rounded-md bg-[#F59E0B] text-[#090D16] font-bold disabled:opacity-40">{busy ? "Verifying…" : "Verify"}</button>
        <button data-testid="totp-signout-btn" onClick={() => signOut().then(() => window.location.reload())} className="mt-2 h-10 w-full text-sm text-[#9CA3AF]">Use a different account</button>
      </div>
    </div>
  );
}

function AuthCallback({ onDone, onError }) {
  const location = useLocation();
  const nav = useNavigate();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(location.hash.slice(1)).get("session_id");
    if (window.opener && window.opener !== window) {
      window.opener.postMessage({ ff_session_id: sid }, window.location.origin);
      window.close();
      return;
    }
    exchange(sid)
      .then((r) => { onDone(r.data); nav("/", { replace: true }); })
      .catch((e) => { onError(errMsg(e)); nav("/", { replace: true }); });
  }, [location.hash, nav, onDone, onError]);
  return <div className={shell} data-testid="auth-callback">Signing you in…</div>;
}

export const AuthContextUser = { current: null };

export default function AuthGate({ children }) {
  const location = useLocation();
  const [me, setMe] = useState(undefined);
  const [error, setError] = useState("");
  const check = useCallback(() => api.get("/auth/me").then((r) => setMe(r.data)).catch(() => setMe(null)), []);
  useEffect(() => { if (!window.location.hash.includes("session_id=")) check(); }, [check]);
  const onDone = useCallback((d) => { setError(""); setMe(d); }, []);
  const onError = useCallback((m) => { setError(m); setMe(null); }, []);
  useEffect(() => {
    const onMsg = (e) => {
      if (e.origin !== window.location.origin || !e.data?.ff_session_id) return;
      setMe(undefined);
      exchange(e.data.ff_session_id).then(onDone).catch((err) => onError(errMsg(err)));
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [onDone, onError]);
  if (location.hash?.includes("session_id=")) return <AuthCallback onDone={onDone} onError={onError} />;
  if (me === undefined) return <div className={shell} data-testid="auth-loading">Loading…</div>;
  if (!me) return <Login error={error} />;
  if (!me.mfa_verified) return <TwoFactor me={me} onDone={setMe} />;
  AuthContextUser.current = { ...me.user, is_super_admin: !!me.is_super_admin };
  return children;
}
